import React from 'react';
import { Card } from './UIComponents';
import { Receipt, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import type { Invoice, PaymentStatus } from '../types';

interface InvoiceListProps {
  invoices: Invoice[];
  total: number;
  currentPage: number;
  totalPages: number;
  onNextPage: () => void;
  onPrevPage: () => void;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  loading?: boolean;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatAmount(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

function getStatusBadge(status: PaymentStatus) {
  switch (status) {
    case 'succeeded':
      return (
        <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300">
          Paid
        </span>
      );
    case 'pending':
      return (
        <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
          Pending
        </span>
      );
    case 'failed':
      return (
        <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
          Failed
        </span>
      );
    case 'refunded':
      return (
        <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-zinc-800 text-slate-800 dark:text-zinc-300">
          Refunded
        </span>
      );
    default:
      return null;
  }
}

const InvoiceList: React.FC<InvoiceListProps> = ({
  invoices,
  total,
  currentPage,
  totalPages,
  onNextPage,
  onPrevPage,
  hasNextPage,
  hasPrevPage,
  loading = false,
}) => {
  if (invoices.length === 0 && !loading) {
    return (
      <Card className="p-8 text-center">
        <Receipt className="h-12 w-12 text-slate-300 dark:text-zinc-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-900 dark:text-white">No invoices yet</h3>
        <p className="mt-2 text-sm text-slate-500 dark:text-zinc-400">
          Your payment history will appear here once you make your first payment.
        </p>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 dark:border-zinc-800">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Payment History</h3>
      </div>

      <div className="divide-y divide-slate-100 dark:divide-zinc-800">
        {invoices.map((invoice) => (
          <div
            key={invoice.id}
            className="px-6 py-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-zinc-800/50 transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="p-2 bg-slate-100 dark:bg-zinc-800 rounded-lg">
                <Receipt className="h-5 w-5 text-slate-600 dark:text-zinc-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900 dark:text-white">
                  {invoice.description || 'Subscription payment'}
                </p>
                <p className="text-xs text-slate-500 dark:text-zinc-400">
                  {formatDate(invoice.paid_at || invoice.created_at)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-slate-900 dark:text-white">
                {formatAmount(invoice.amount, invoice.currency)}
              </span>
              {getStatusBadge(invoice.status)}
              {invoice.receipt_url && (
                <a
                  href={invoice.receipt_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-slate-400 dark:text-zinc-500 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  title="View receipt"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-slate-100 dark:border-zinc-800 flex items-center justify-between">
          <p className="text-sm text-slate-500 dark:text-zinc-400">
            Showing {invoices.length} of {total} invoices
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onPrevPage}
              disabled={!hasPrevPage || loading}
              className="p-2 rounded-lg border border-slate-200 dark:border-zinc-700 text-slate-600 dark:text-zinc-400 hover:bg-slate-50 dark:hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-slate-600 dark:text-zinc-400">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={onNextPage}
              disabled={!hasNextPage || loading}
              className="p-2 rounded-lg border border-slate-200 dark:border-zinc-700 text-slate-600 dark:text-zinc-400 hover:bg-slate-50 dark:hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default InvoiceList;
